import { useEffect, useState } from 'react';

/** True unless the browser positively reports no connection. */
function navigatorOnline(): boolean {
  if (typeof navigator === 'undefined') return true;
  return navigator.onLine !== false;
}

/**
 * Tracks `navigator.onLine` (issue #376).
 *
 * `navigator.onLine` is only a hint — it is famously true on a captive portal,
 * and a `false` is the reliable half. The initial value therefore treats
 * `undefined`/missing as online, and only an explicit `false` switches to the
 * offline page. Being wrong in that direction shows a stale page; being wrong
 * in the other hides a working app behind an offline notice.
 *
 * The `online`/`offline` events are the real signal: they fire when the
 * connection state changes, so a reader who reconnects recovers without a
 * reload.
 */
export function useOnline(): boolean {
  const [online, setOnline] = useState<boolean>(navigatorOnline);
  useEffect(() => {
    const update = (): void => {
      setOnline(navigatorOnline());
    };
    // Read once on mount as well as on events: the value can change between the
    // initial render and the effect being attached.
    update();
    window.addEventListener('online', update);
    window.addEventListener('offline', update);
    return () => {
      window.removeEventListener('online', update);
      window.removeEventListener('offline', update);
    };
  }, []);
  return online;
}
